import TomSelect from 'tom-select';
import flatpickr from 'flatpickr'
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import { Japanese } from "flatpickr/dist/l10n/ja.js"

/**
 * Build a Navitime vertex JSON structure from a spot object.
 * @param {{coord:{lon:number,lat:number},node?:string,stay_time?:number,name?:string}} spot
 * @returns {{lon:number,lat:number,name?:string,node?:string,['stay-time']?:number}}
 */
export function createVertex(spot) {
  const vertex = { lon: spot.coord.lon, lat: spot.coord.lat, name: spot.name }
  if (spot.node) vertex.node = spot.node
  if (spot.stay_time) vertex['stay-time'] = spot.stay_time
  return vertex
}

/**
 * Parse route sections from Navitime response and synthesize display lines.
 * Matches legacy behavior; currently not returned.
 *
 * @param {Array} sections
 */
export function drawRouteSections(sections = []) {
  let parsed = [], tmp = {}, maxIndex = sections.length - 1

  sections.forEach(function (section, i) {
    if (section.type === 'point') {
      if (section.with_via) {
        tmp.name_style = 'font-weight:bold;'
        tmp.stopper_color = '#3cb371'
        tmp.line_style = 'display: show;'
      }
      if (section.img) {
        tmp.img = section.img
        tmp.show_weather = true
        tmp.id = String(i)
        if (i !== 0) tmp.id = String(i / 2)
      }
      tmp.point_name = section.name
    } else {
      tmp.link_name = section.line_name
      tmp.duration = '（' + section.time + '分）'
      tmp.from_time = section.from_time
      tmp.to_time = section.to_time
    }

    if (i === 0) {
      tmp.name_style = 'font-weight:bold'
      tmp.stopper_color = '#4169e1'
    } else if (i === maxIndex) {
      tmp.name_style = 'font-weight:bold'
      tmp.stopper_color = '#cd5c5c'
      tmp.line_style = 'display: none;'
      parsed.push(tmp)
    } else if (section.type === 'move') {
      parsed.push(tmp)
      tmp = {}
    }
  })

  return parsed
}

/**
 * Transform Navitime route sections into trip metrics and visit times.
 * @param {Array} data Route sections (with type: 'point' | 'move')
 * @param {number} startTime Start time in ms since midnight
 * @returns {{travelTime: string, visits: Array<{id:number,name:string,arrivalTime:string,departureTime:string}>}}
 */
import { toTime } from './time-utils'

export function tripData(data, startTime) {
  const points = []
  const moves = []
  const visits = []
  let currentTime = startTime
  let totalTravelTime = 0

  data.forEach((item) => {
    if (item.type === 'point') {
      points.push(item)
    } else if (item.type === 'move') {
      moves.push(item)
      totalTravelTime += item.time
    }
  })

  if (points.length > 0) {
    const startingPoint = points[0]
    visits.push({ id: 1, name: startingPoint.name, arrivalTime: currentTime - 20 * 60000, departureTime: currentTime })
  }

  for (let i = 1; i < points.length; i++) {
    if (i - 1 < moves.length) {
      const moveTime = moves[i - 1].time
      const arrivalTime = currentTime + moveTime * 60000
      const departureTime = arrivalTime + 20 * 60000
      visits.push({ id: i + 1, name: points[i].name, arrivalTime, departureTime })
      currentTime = departureTime
    }
  }

  return {
    travelTime: toTime(totalTravelTime * 60000),
    visits: visits.map((value) => ({ ...value, arrivalTime: toTime(value.arrivalTime), departureTime: toTime(value.departureTime) }))
  }
}

export function createTomSelectElement(element) {
	if (!element || element.tomselect) {
		return element?.tomselect;
	}
	let plugins = ['dropdown_input'];
	if (element.hasAttribute('multiple') ||
		element.hasAttribute('data-clearable')) {
		plugins.push('remove_button');
	}
	new TomSelect(element, {plugins});
}

export function createDatePicker(element, data) {
	if (!element) {
		return null;
	}

	const inputType = (element.getAttribute('type') || '').toLowerCase();
	const explicitFormat = data?.format || element.dataset.format;
	const isMonthPicker = explicitFormat === 'yyyy-mm' || inputType === 'month' || element.classList.contains('month-picker');
	const isTimePicker = inputType === 'time' || element.classList.contains('time-picker');
	const isDateTimePicker = inputType === 'datetime-local' || element.classList.contains('datetime-picker');

	const dispatchValueChange = () => {
		element.dispatchEvent(new Event('input', {bubbles: true}));
		element.dispatchEvent(new Event('change', {bubbles: true}));
	};

	const options = {
		"locale": Japanese,
		allowInput: true,
		disableMobile: true,
		onChange: dispatchValueChange,
		onValueUpdate: dispatchValueChange
	};

	if (isMonthPicker) {
		Object.assign(options, {
			dateFormat: 'Y-m',
			plugins: [
				monthSelectPlugin({
					shorthand: true,
					dateFormat: 'Y-m',
					altFormat: 'F Y'
				})
			]
		});
	} else if (isTimePicker) {
		Object.assign(options, {
			enableTime: true,
			noCalendar: true,
			time_24hr: true,
			dateFormat: 'H:i'
		});
	} else if (isDateTimePicker) {
		Object.assign(options, {
			enableTime: true,
			time_24hr: true,
			dateFormat: 'Y-m-d\\TH:i'
		});
	} else {
		Object.assign(options, {
			dateFormat: 'Y-m-d'
		});
	}

	return flatpickr(element, options);
}

export default { createVertex, drawRouteSections, tripData }
